import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserGroup,
  faBriefcase,
  faAddressCard,
  faStore,
  faCircleCheck,
  faDollarSign,
  faEdit,
  faPlus,
  faMinus,
  faCog,
  faFileLines,
  faPhotoFilm,
  faGift,
} from '@fortawesome/free-solid-svg-icons';
import { ActivitySubType } from '@frontend/app/types/globalTypes';
import { ImagePolaroidIcon, PenPaintbrushIcon } from '@revfluence/fresh-icons/solid/esm';

export interface IconConfig {
  icon: React.ReactNode;
}

// Activity Icons Configuration - simplified to use ActivitySubType directly
export const ACTIVITY_ICONS: Partial<Record<ActivitySubType, IconConfig>> = {
  // Application activities
  [ActivitySubType.APPLICATION_SUBMITTED]: {
    icon: <FontAwesomeIcon icon={faUserGroup} className="h-3 text-blue-500" />,
  },
  [ActivitySubType.APPLICATION_APPROVED]: {
    icon: <FontAwesomeIcon icon={faUserGroup} className="h-3 text-green-500" />,
  },
  [ActivitySubType.APPLICATION_REJECTED]: { icon: <FontAwesomeIcon icon={faUserGroup} className="h-3 text-red-500" /> },

  // Member info activities
  [ActivitySubType.MEMBER_INFO_REQUEST_RECEIVED]: {
    icon: <FontAwesomeIcon icon={faAddressCard} className="h-3 text-blue-500" />,
  },

  // Marketplace activities
  [ActivitySubType.MARKETPLACE_PUBLISHED]: { icon: <FontAwesomeIcon icon={faStore} className="h-3 text-yellow-500" /> },
  [ActivitySubType.MARKETPLACE_UNPUBLISHED]: {
    icon: <FontAwesomeIcon icon={faStore} className="h-3 text-yellow-500" />,
  },
  [ActivitySubType.MARKETPLACE_AUTO_UNPUBLISHED]: {
    icon: <FontAwesomeIcon icon={faStore} className="h-3 text-orange-500" />,
  },

  // Stage activities
  [ActivitySubType.STAGE_COMPLETED]: { icon: <FontAwesomeIcon icon={faCircleCheck} className="h-3 text-green-500" /> },
  [ActivitySubType.STAGE_MOVED]: { icon: <FontAwesomeIcon icon={faCog} className="h-3 text-blue-500" /> },

  // Custom stage activities
  [ActivitySubType.CUSTOM_STAGE_COMPLETED]: {
    icon: <div className="text-gray-500"><PenPaintbrushIcon className="h-3 w-3" /></div>,
  },
  [ActivitySubType.CUSTOM_STAGE_STATUS_CHANGED]: {
    icon: <div className="text-gray-500"><PenPaintbrushIcon className="h-3 w-3" /></div>,
  },

  // Brief activities
  [ActivitySubType.BRIEF_UPDATED]: { icon: <FontAwesomeIcon icon={faFileLines} className="h-3 text-purple-5" /> },
  [ActivitySubType.BRIEF_SIGNED]: { icon: <FontAwesomeIcon icon={faFileLines} className="h-3 text-purple-5" /> },

  // Content activities
  [ActivitySubType.CONTENT_SUBMITTED]: { icon: <FontAwesomeIcon icon={faPhotoFilm} className="h-3 text-pink-5" /> },
  [ActivitySubType.CONTENT_APPROVED]: { icon: <FontAwesomeIcon icon={faPhotoFilm} className="h-3 text-pink-5" /> },
  [ActivitySubType.CONTENT_REJECTED]: { icon: <FontAwesomeIcon icon={faPhotoFilm} className="h-3 text-pink-5" /> },
  [ActivitySubType.CONTENT_AUTO_APPROVED]: {
    icon: <FontAwesomeIcon icon={faPhotoFilm} className="h-3 text-pink-5" />,
  },

  [ActivitySubType.LIVE_POST_UPLOADED]: { icon: <div className="text-pink-4"><ImagePolaroidIcon className="h-3 w-3" /></div>, },

  // Commission activities
  [ActivitySubType.COMMISSION_EARNED]: { icon: <FontAwesomeIcon icon={faDollarSign} className="h-3 text-green-500" /> },
  [ActivitySubType.COMMISSION_PAID]: { icon: <FontAwesomeIcon icon={faDollarSign} className="h-3 text-green-600" /> },

  // Product activities
  [ActivitySubType.CREATOR_ORDER_REQUEST_APPROVED]: { icon: <FontAwesomeIcon icon={faGift} className="h-3 text-orange-5" /> },
  [ActivitySubType.CREATOR_ORDER_REQUEST_AUTO_APPROVED]: { icon: <FontAwesomeIcon icon={faGift} className="h-3 text-orange-5" /> },
  [ActivitySubType.CREATOR_ORDER_REQUEST_CREATED]: { icon: <FontAwesomeIcon icon={faGift} className="h-3 text-orange-5" /> },
  [ActivitySubType.CREATOR_ORDER_REQUEST_REJECTED]: { icon: <FontAwesomeIcon icon={faGift} className="h-3 text-orange-5" /> },
  [ActivitySubType.PRODUCT_SHIPPED]: { icon: <FontAwesomeIcon icon={faGift} className="h-3 text-orange-5" /> },

  // Program activities
  [ActivitySubType.PROGRAM_CREATED]: { icon: <FontAwesomeIcon icon={faBriefcase} className="h-3 text-blue-500" /> },
  [ActivitySubType.PROGRAM_UPDATED]: { icon: <FontAwesomeIcon icon={faEdit} className="h-3 text-yellow-500" /> },
  [ActivitySubType.PROGRAM_MEMBER_ADDED]: { icon: <FontAwesomeIcon icon={faPlus} className="h-3 text-green-500" /> },
  [ActivitySubType.PROGRAM_MEMBER_REMOVED]: { icon: <FontAwesomeIcon icon={faMinus} className="h-3 text-red-500" /> },
} as const;

const DEFAULT_ICON_CONFIG: IconConfig = {
  icon: <FontAwesomeIcon icon={faBriefcase} className="h-3 text-gray-500" />,
};

export const getActivityIcon = (subType: string): React.ReactNode => {
  const config = ACTIVITY_ICONS[subType as ActivitySubType] || DEFAULT_ICON_CONFIG;

  return (
    <div className="w-6 h-6 p-3 rounded-lg flex justify-center items-center gap-2 overflow-hidden">{config.icon}</div>
  );
};
